import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { AppStatusBadge, StatusBadge } from "@/components/Badges";
import { appApi } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Plus, FileText, Loader2, ArrowRight, RefreshCw, Clock } from "lucide-react";

const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [renewing, setRenewing] = useState(null);

  useEffect(() => {
    appApi.list().then(({ data }) => setApps(data)).finally(() => setLoading(false));
  }, []);

  const handleRenew = async (id) => {
    setRenewing(id);
    try {
      const { data } = await appApi.renew(id);
      toast.success("Renewal application created — update your documents and re-submit");
      navigate(`/applications/${data.id}`);
    } catch {
      toast.error("Could not start renewal");
    } finally { setRenewing(null); }
  };

  return (
    <div className="wechr-section">
      <div className="wechr-container">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl font-semibold text-navy-900">Your applications</h1>
            <p className="text-sm text-[var(--wechr-muted-text)]">Welcome back, {user?.name}. Track and manage your certification applications.</p>
          </div>
          <Button className="btn-green" onClick={() => navigate("/apply")} data-testid="dashboard-new-application-button"><Plus className="mr-2 h-4 w-4" /> New application</Button>
        </div>

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-wgreen-600" /></div>
        ) : apps.length === 0 ? (
          <Card className="mt-8"><CardContent className="flex flex-col items-center gap-3 py-16 text-center">
            <FileText className="h-10 w-10 text-[var(--wechr-muted-text)]" />
            <p className="font-medium text-navy-900">No applications yet</p>
            <p className="max-w-md text-sm text-[var(--wechr-muted-text)]">Start your WECH-RB DD Certification by creating your first application.</p>
            <Button className="btn-green" onClick={() => navigate("/apply")}>Start an application</Button>
          </CardContent></Card>
        ) : (
          <div className="mt-8 grid gap-4">
            {apps.map((a) => (
              <Card key={a.id} data-testid="dashboard-application-card">
                <CardContent className="p-6">
                  <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                    <div>
                      <div className="flex flex-wrap items-center gap-3">
                        <h3 className="font-heading text-base font-semibold text-navy-900">{a.company_name}</h3>
                        <AppStatusBadge status={a.status} />
                        {a.certificate && <StatusBadge status={a.certificate.status} />}
                      </div>
                      <p className="mt-1 text-sm text-[var(--wechr-muted-text)]">{a.industry} · {a.country} · Requested: {a.requested_level}</p>
                      {a.certificate && <p className="mt-1 text-sm text-navy-800">Certificate <span className="font-mono">{a.certificate.certificate_id}</span> · expires {a.certificate.expiry_date}</p>}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {a.certificate_id && <Link to={`/certificate/${a.certificate_id}`}><Button variant="outline" size="sm">Certificate</Button></Link>}
                      <Link to={`/applications/${a.id}`}><Button className="btn-green" size="sm">Open <ArrowRight className="ml-1 h-4 w-4" /></Button></Link>
                    </div>
                  </div>
                  {a.certificate && a.certificate.expiring_soon && (
                    <div className="mt-4 flex flex-col gap-2 rounded-lg border border-gold-500 bg-gold-100 p-3 text-sm text-gold-700 sm:flex-row sm:items-center sm:justify-between" data-testid="renewal-banner">
                      <span className="flex items-center gap-2"><Clock className="h-4 w-4" /> This certificate expires in <strong>{a.certificate.days_left} days</strong>. Renew now to stay certified.</span>
                      <Button size="sm" className="btn-green" onClick={() => handleRenew(a.id)} disabled={renewing === a.id} data-testid="dashboard-renew-button">
                        {renewing === a.id ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-1 h-4 w-4" />} Renew now
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
